'use client';
import { useState, useEffect } from 'react';
import Link from 'next/link';
import { 
  BarChart3, TrendingUp, Users, Package, FileText, 
  RefreshCw, DollarSign, Activity, AlertCircle, ShoppingCart, 
  ArrowRight, Star, Trash2
} from 'lucide-react';

export default function ExecutiveDashboard() {
  const [syncing, setSyncing] = useState(false);
  const [shortcuts, setShortcuts] = useState<any[]>([]);
  const [summaryData, setSummaryData] = useState({
    todaySales: 0,
    inventoryCount: 0,
    outstandingAR: 0,
    pendingPOs: 0
  });
  const [loadingSummary, setLoadingSummary] = useState(true);

  useEffect(() => {
    try {
      const saved = JSON.parse(localStorage.getItem('dashboard_shortcuts') || '[]');
      setShortcuts(saved);
    } catch(e) {}
    
    // Fetch summary data
    fetch('/api/dashboard/summary')
      .then(res => res.json())
      .then(data => {
        if (data.success) {
          setSummaryData(data.data);
        }
      })
      .catch(err => console.error("Error fetching summary:", err))
      .finally(() => setLoadingSummary(false));
  }, []);

  const removeShortcut = (idx: number) => {
    const newShortcuts = [...shortcuts];
    newShortcuts.splice(idx, 1);
    setShortcuts(newShortcuts);
    localStorage.setItem('dashboard_shortcuts', JSON.stringify(newShortcuts));
  };
  
  const handleSync = () => {
    setSyncing(true);
    setTimeout(() => setSyncing(false), 2000);
  };

  return (
    <main className="p-8 max-w-[1600px] mx-auto min-h-screen text-slate-100 font-sans">
      <header className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-8">
        <div>
          <h1 className="text-3xl font-extrabold flex items-center gap-3 tracking-tight">
            <Activity className="text-blue-500 h-9 w-9" /> Executive Dashboard
          </h1>
          <p className="text-slate-400 text-sm mt-1">
            ภาพรวมระบบบริหารจัดการ Express ERP
          </p>
        </div>
        <div className="flex gap-3">
          <button 
            onClick={handleSync}
            disabled={syncing}
            className="px-4 py-2 bg-slate-800 hover:bg-slate-700 text-white rounded-lg flex items-center gap-2 transition"
          >
            <RefreshCw size={18} className={syncing ? "animate-spin" : ""} />
            {syncing ? "กำลังซิงค์..." : "ซิงค์ข้อมูลล่าสุด"}
          </button>
        </div>
      </header>
      
      {/* 🌟 Shortcuts Section */}
      {shortcuts.length > 0 && (
        <div className="mb-8 bg-slate-900 border border-slate-800 rounded-2xl p-6 shadow-xl relative overflow-hidden">
          <div className="flex items-center gap-2 mb-4">
            <Star className="text-amber-400" size={24} />
            <h2 className="text-xl font-bold text-white">เมนูลัด (Shortcuts)</h2>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
            {shortcuts.map((sc, idx) => (
              <div key={idx} className="relative group">
                <Link href={sc.path} className="flex flex-col items-center justify-center bg-slate-800 hover:bg-slate-700 hover:border-amber-500/50 border border-slate-700 transition p-4 rounded-xl text-center h-full shadow-md">
                  <Star size={28} className="text-amber-400 mb-2 group-hover:scale-110 transition" />
                  <span className="text-xs font-bold text-slate-200 line-clamp-2">{sc.title}</span>
                </Link>
                <button 
                  onClick={() => removeShortcut(idx)}
                  className="absolute -top-2 -right-2 bg-rose-500 hover:bg-rose-600 text-white rounded-full p-1.5 opacity-0 group-hover:opacity-100 transition shadow-lg"
                  title="ลบเมนูลัดนี้"
                >
                  <Trash2 size={12} />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <div className="bg-slate-900 border border-slate-800 rounded-2xl p-6 shadow-xl relative overflow-hidden">
          <div className="absolute top-0 right-0 p-4 opacity-10">
            <DollarSign size={80} />
          </div>
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 rounded-full bg-emerald-500/20 flex items-center justify-center text-emerald-400">
              <TrendingUp size={20} />
            </div>
            <h3 className="text-slate-400 font-medium">ยอดขายวันนี้</h3>
          </div>
          <p className="text-3xl font-bold text-white mb-2">
            {loadingSummary ? '...' : `฿ ${summaryData.todaySales.toLocaleString('en-US', {minimumFractionDigits: 2})}`}
          </p>
          <p className="text-xs text-emerald-400 flex items-center gap-1">
            <TrendingUp size={12} /> ข้อมูลอัปเดตล่าสุด
          </p>
        </div>

        <div className="bg-slate-900 border border-slate-800 rounded-2xl p-6 shadow-xl relative overflow-hidden">
          <div className="absolute top-0 right-0 p-4 opacity-10">
            <Package size={80} />
          </div>
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 rounded-full bg-blue-500/20 flex items-center justify-center text-blue-400">
              <Package size={20} />
            </div>
            <h3 className="text-slate-400 font-medium">สินค้าคงเหลือ (IU)</h3>
          </div>
          <p className="text-3xl font-bold text-white mb-2">{loadingSummary ? '...' : `${summaryData.inventoryCount.toLocaleString()} รายการ`}</p>
          <Link href="/inventory" className="text-xs text-blue-400 hover:text-blue-300 flex items-center gap-1">
            ดูรายงานสินค้าคงเหลือ <ArrowRight size={12} />
          </Link>
        </div>

        <div className="bg-slate-900 border border-slate-800 rounded-2xl p-6 shadow-xl relative overflow-hidden">
          <div className="absolute top-0 right-0 p-4 opacity-10">
            <Users size={80} />
          </div>
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 rounded-full bg-purple-500/20 flex items-center justify-center text-purple-400">
              <Users size={20} />
            </div>
            <h3 className="text-slate-400 font-medium">ลูกหนี้คงค้าง</h3>
          </div>
          <p className="text-3xl font-bold text-white mb-2">{loadingSummary ? '...' : `฿ ${summaryData.outstandingAR.toLocaleString('en-US', {minimumFractionDigits: 2})}`}</p>
          <Link href="/reports/customer-balances" className="text-xs text-purple-400 hover:text-purple-300 flex items-center gap-1">
            ดูรายงานลูกหนี้ <ArrowRight size={12} />
          </Link>
        </div>

        <div className="bg-slate-900 border border-slate-800 rounded-2xl p-6 shadow-xl relative overflow-hidden">
          <div className="absolute top-0 right-0 p-4 opacity-10">
            <AlertCircle size={80} />
          </div>
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 rounded-full bg-orange-500/20 flex items-center justify-center text-orange-400">
              <ShoppingCart size={20} />
            </div>
            <h3 className="text-slate-400 font-medium">ใบสั่งซื้อรอรับ</h3>
          </div>
          <p className="text-3xl font-bold text-white mb-2">{loadingSummary ? '...' : `${summaryData.pendingPOs} ใบ`}</p>
          <Link href="/purchase" className="text-xs text-orange-400 hover:text-orange-300 flex items-center gap-1">
            ตรวจสอบใบสั่งซื้อ <ArrowRight size={12} />
          </Link>
        </div>
      </div>

      {/* Quick Links */}
      <h2 className="text-xl font-bold mb-4 flex items-center gap-2"><BarChart3 size={20} className="text-blue-400" /> เมนูลัด (Quick Access)</h2>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Link href="/reports/customer-balances" className="bg-slate-800 hover:bg-slate-700 p-4 rounded-xl transition flex flex-col items-center justify-center gap-2 text-center group border border-slate-700">
          <FileText className="text-slate-400 group-hover:text-blue-400 transition" size={28} />
          <span className="text-sm font-medium text-slate-300">วิเคราะห์กำไร / ลูกหนี้</span>
        </Link>
        <Link href="/inventory" className="bg-slate-800 hover:bg-slate-700 p-4 rounded-xl transition flex flex-col items-center justify-center gap-2 text-center group border border-slate-700">
          <Package className="text-slate-400 group-hover:text-blue-400 transition" size={28} />
          <span className="text-sm font-medium text-slate-300">คลังสินค้า (S/N)</span>
        </Link>
        <Link href="/purchase" className="bg-slate-800 hover:bg-slate-700 p-4 rounded-xl transition flex flex-col items-center justify-center gap-2 text-center group border border-slate-700">
          <ShoppingCart className="text-slate-400 group-hover:text-blue-400 transition" size={28} />
          <span className="text-sm font-medium text-slate-300">ระบบจัดซื้อ (PO)</span>
        </Link>
        <Link href="/chat" className="bg-slate-800 hover:bg-slate-700 p-4 rounded-xl transition flex flex-col items-center justify-center gap-2 text-center group border border-slate-700">
          <Users className="text-slate-400 group-hover:text-blue-400 transition" size={28} />
          <span className="text-sm font-medium text-slate-300">แชททีม</span>
        </Link>
      </div>
      
      <div className="mt-12 text-center text-slate-500 text-xs">
        <p>Express ERP Dashboard Version 2.0</p>
        <p>เชื่อมต่อกับฐานข้อมูล Express สำเร็จ</p>
      </div>
    </main>
  );
}
